<template>
  <b-collapse
    :visible="expanded"
    appear
    @hidden="$emit('collapsed')"
  >
    <div class="poy-detail-wrap">
      <div
        class="poy-detail-size"
        :style="contentHeight ? { height: `${contentHeight}px` } : {}"
      >
        <section
          :id="`poy-detail-${txId}`"
          ref="content"
          class="poy-details"
          :aria-label="$t('poyReport.details')"
          :aria-busy="state.loading ? 'true' : 'false'"
        >
          <div
            v-if="state.loading"
            class="text-center p-3"
            role="status"
          >
            <b-spinner
              small
              class="mr-50"
            />{{ $t('กำลังโหลดข้อมูล...') }}
          </div>
          <b-alert
            :show="!!state.error"
            variant="danger"
            class="mb-0"
          >
            <div class="d-flex align-items-center justify-content-between p-1">
              <span>{{ state.error }}</span>
              <b-button
                size="sm"
                variant="outline-danger"
                @click="$emit('retry')"
              >
                {{ $t('poyReport.retry') }}
              </b-button>
            </div>
          </b-alert>
          <template v-if="state.data && !state.loading && !state.error">
            <div class="poy-detail-heading">
              <div>
                <h5 class="mb-50">
                  {{ $t('poyReport.details') }} <span class="text-primary">#{{ state.data.bill.betId || '—' }}</span>
                </h5>
                <span>{{ state.data.bill.username }} ·
                  <span class="poy-detail-lottery">
                    <span
                      v-if="hasFlag(state.data.bill.huayName)"
                      class="flag-icon flag-icon-rounded poy-lottery-icon"
                      :class="getFlagIconClass(state.data.bill.huayName)"
                      aria-hidden="true"
                    />
                    <span>{{ state.data.bill.huayName }}</span>
                  </span>
                  · {{ $t('Round ID') }} {{ state.data.bill.roundId }}
                </span>
              </div>
              <div class="d-flex align-items-center flex-shrink-0">
                <b-button
                  :href="link"
                  target="_blank"
                  rel="noopener"
                  size="sm"
                  variant="flat-secondary"
                  class="poy-detail-action mr-50"
                  :aria-label="$t('poyReport.openLink')"
                >
                  <feather-icon
                    icon="ExternalLinkIcon"
                    size="14"
                  />
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="poy-detail-action"
                  @click="$emit('collapse')"
                >
                  <feather-icon
                    icon="ChevronUpIcon"
                    size="14"
                    class="mr-25"
                  />{{ $t('poyReport.collapse') }}
                </b-button>
              </div>
            </div>
            <div class="poy-detail-summary">
              <span>{{ $t('poyReport.itemCount') }} <strong>{{ state.data.bill.itemCount }}</strong></span>
              <span>{{ $t('Bet Amount') }} <strong class="poy-detail-amount">{{ money(state.data.bill.betAmount) }}</strong></span>
              <span>{{ $t('poyReport.cashback') }} <strong class="poy-detail-amount">{{ money(state.data.bill.cashBackAmount) }}</strong></span>
              <span>{{ $t('poyReport.payout') }} <strong
                class="poy-detail-amount"
                :class="{ 'report-text-success': state.data.bill.winAmount > 0 }"
              >{{ money(state.data.bill.winAmount) }}</strong></span>
              <span v-if="scoped">{{ $t('downlineReports.settlementBet') }} <strong class="poy-detail-amount">{{ money(state.data.bill.settlementBet) }}</strong></span>
            </div>
            <p
              v-if="query.number || query.betType"
              class="poy-muted px-1 mb-1"
            >
              {{ $t('poyReport.matchHint') }}
            </p>
            <b-table
              :items="visibleItems"
              :fields="fields"
              :tbody-tr-class="itemClass"
              responsive
              small
              bordered
              class="report-table-colors report-table-header mb-0"
            >
              <template #cell(betType)="{ item }">
                {{ typeLabel(item) }}
              </template>
              <template #cell(betNumber)="{ value }">
                <strong>{{ value }}</strong>
              </template>
              <template #cell(payRate)="{ value }">
                {{ money(value) }}
              </template>
              <template #cell(betAmount)="{ value }">
                <strong class="poy-detail-amount">{{ money(value) }}</strong>
              </template>
              <template #cell(cashBackAmount)="{ value }">
                {{ money(value) }}
              </template>
              <template #cell(winAmount)="{ value }">
                <span
                  class="poy-detail-amount"
                  :class="{ 'report-text-success': value > 0 }"
                >{{ money(value) }}</span>
              </template>
              <template #cell(settlementBet)="{ value }">
                <span class="poy-detail-amount">{{ money(value) }}</span>
              </template>
              <template #cell(status)="{ value }">
                <b-badge
                  :variant="statusVariant(value)"
                  :class="{ 'report-status-win': value === 1, 'report-status-loss': value === 2 }"
                >
                  {{ $t(statusKey(value)) }}
                </b-badge>
              </template>
            </b-table>
            <div class="d-flex flex-wrap align-items-center justify-content-between p-1">
              <span class="poy-muted">{{ $t('poyReport.showingItems', { shown: visibleItems.length, total: items.length }) }}</span>
              <b-button
                v-if="items.length > 10"
                variant="flat-primary"
                size="sm"
                @click="showAll = !showAll"
              >
                {{ showAll ? $t('poyReport.showLess') : $t('poyReport.showAll', { count: items.length }) }}
              </b-button>
            </div>
          </template>
        </section>
      </div>
    </div>
  </b-collapse>
</template>

<script>
import {
  BAlert, BBadge, BButton, BCollapse, BSpinner, BTable,
} from 'bootstrap-vue'
import { countryFlagMixin } from '@core/mixins/ui/countryFlag'
import {
  betTypeLabel, money, statusKey, statusVariant,
} from '@/utils/poyReport'

export default {
  components: {
    BAlert, BBadge, BButton, BCollapse, BSpinner, BTable,
  },
  mixins: [countryFlagMixin],
  props: {
    txId: { type: String, required: true },
    expanded: { type: Boolean, default: false },
    link: { type: String, default: '' },
    state: { type: Object, required: true },
    query: { type: Object, required: true },
    lotteries: { type: Array, default: () => [] },
    scoped: { type: Boolean, default: false },
  },
  data: () => ({ showAll: false, contentHeight: 0, resizeObserver: null }),
  computed: {
    items() { return this.state.data?.items || [] },
    visibleItems() {
      // Keep matching lines visible even when a long bill is initially folded.
      const rows = this.query.number || this.query.betType
        ? [...this.items.filter(this.matches), ...this.items.filter(v => !this.matches(v))] : this.items
      return this.showAll ? rows : rows.slice(0, 10)
    },
    fields() {
      const columns = [
        ['betType', 'Bet Type'], ['betNumber', 'poyReport.number'], ['payRate', 'อัตราจ่าย'],
        ['betAmount', 'Bet Amount'], ['cashBackAmount', 'poyReport.cashback'], ['winAmount', 'poyReport.payout'],
      ]
      if (this.scoped) columns.push(['settlementBet', 'downlineReports.settlementBet'])
      columns.push(['status', 'Status'])
      return columns.map(([key, label]) => ({ key, label: this.$t(label), class: ['betType', 'betNumber', 'status'].includes(key) ? 'text-nowrap' : 'text-right text-nowrap' }))
    },
  },
  mounted() {
    if (typeof ResizeObserver === 'undefined') return
    this.resizeObserver = new ResizeObserver(() => {
      const height = this.$refs.content?.getBoundingClientRect().height
      if (height > 0) this.contentHeight = Math.ceil(height)
    })
    this.resizeObserver.observe(this.$refs.content)
  },
  beforeDestroy() { if (this.resizeObserver) this.resizeObserver.disconnect() },
  methods: {
    money,
    statusKey,
    statusVariant,
    typeLabel(item) {
      const lottery = this.lotteries.find(v => String(v.id) === String(item.huayId))
      return this.$t(betTypeLabel(item.huayId, item.betType, lottery?.type))
    },
    matches(item) {
      return (!this.query.number || item.betNumber === this.query.number)
        && (!this.query.betType || item.betType === Number(this.query.betType))
    },
    itemClass(item) {
      return item && (this.query.number || this.query.betType) && this.matches(item) ? 'poy-match' : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.poy-detail-wrap { padding: 1rem; }
.poy-detail-size { overflow: hidden; transition: height 220ms cubic-bezier(0.2, 0.8, 0.2, 1); }
.poy-details { --report-label-text: var(--report-strong-text); border: 1px solid var(--report-border); border-radius: 0.4rem; overflow: hidden; }
.dark-layout .poy-details {
  .poy-detail-action:not(:disabled):not(.disabled) {
    color: var(--hdg-ink, #d0d2d6);

    &:hover,
    &:focus {
      color: #f0f1f4;
      background-color: rgba(208, 210, 214, 0.08);
    }
    &:active { background-color: rgba(208, 210, 214, 0.14); }
    &:focus-visible { outline: 2px solid var(--hdg-brand-primary-emphasis, #ff5268); outline-offset: 2px; }
  }
}
.poy-detail-amount { color: var(--report-strong-text); }
.poy-detail-heading { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-start; gap: 1rem; padding: 1rem; background: var(--report-detail-bg); overflow-wrap: anywhere; }
.poy-detail-lottery { display: inline-flex; align-items: baseline; gap: 0.5rem; vertical-align: baseline; }
.poy-detail-lottery > .poy-lottery-icon { align-self: center; }
.poy-detail-summary { display: flex; flex-wrap: wrap; gap: 0.5rem 1.5rem; padding: 1rem; }
.poy-detail-summary strong { margin-left: 0.4rem; font-variant-numeric: tabular-nums; }
::v-deep .poy-match { background: var(--report-match-bg); }
.poy-details ::v-deep .table th,
.poy-details ::v-deep .table td { padding-left: 1rem; padding-right: 1rem; }
.poy-details ::v-deep .table td { padding-top: 0.6rem; padding-bottom: 0.6rem; }
.poy-details ::v-deep .table thead th { color: var(--report-strong-text); }
@media (prefers-reduced-motion: reduce) { .poy-detail-size { transition: none; } }
</style>
